import React from "react";
import { toast } from "react-toastify";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FileUploader } from "react-drag-drop-files";
import Title from "designs/v1/components/Title";
import Button from "designs/v1/components/Button";
import {
  validateIncomeProof,
  uploadReactivateIncomeApi,
} from "designs/v1/pages/ModifyKyc/services";
import { jsonToFormData } from "utils";

const IncomeProof = ({
  activeUserDetail,
  isLoading,
  setIsLoading,
  setStep,
}) => {
  const PROOF_TYPES = ["Bank Statement", "ITR", "Payslip"];
  const ANNUAL_TYPES = [
    "Below 1 Lakh",
    "1-5 Lakh",
    "5-10 Lakh",
    "10-25 Lakh",
    "Above 25 Lakh",
  ];
  const initialValues = {
    anual_income: "",
    icp_type: "",
    icp_proof: "",
    is_encrypted: 0,
  };

  const handleValidate = (values) => {
    const errors = {};
    if (!values.anual_income) {
      errors.anual_income = "Annual income is required.";
    }
    if (
      !values.icp_type &&
      ANNUAL_TYPES.slice(2).includes(values.anual_income)
    ) {
      errors.icp_type = "Proof type is required.";
    }
    if (
      !values.icp_proof &&
      ANNUAL_TYPES.slice(2).includes(values.anual_income)
    ) {
      errors.icp_proof = "Proof file is required.";
    }
    return errors;
  };

  const uploadFile = async (file, setFieldValue) => {
    if (!file) return;
    const allowed_types = ["application/pdf"];
    if (!allowed_types.includes(file.type)) {
      toast.error("Only PDF are allowed", "Error");
      return;
    }
    const fileSize = file.size / 1024 ** 2;
    if (fileSize >= 4) {
      toast.error("Please upload a file smaller than 4 MB");
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    try {
      formData.append("file", file);
      const validate = await validateIncomeProof(formData);
      const isEncrypted = validate.data?.message || false;
      if (isEncrypted) {
        setFieldValue("is_encrypted", 1);
      } else {
        setFieldValue("is_encrypted", 0);
      }
      setFieldValue("icp_proof", file);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (value) => {
    setIsLoading(true);
    const formValue = {
      request_type: "Reactivation",
      doc_name: activeUserDetail?.request_number,
      ...value,
    };
    try {
      await uploadReactivateIncomeApi(jsonToFormData(formValue));
      toast.success("Income proof uploaded successfully.");
      setIsLoading(false);
      setStep("VisualVerification");
    } catch (error) {
      const { data } = error.response || {};
      toast.error(data.message || "Internal server error!");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Title title="Income Proof" />
      <div className="row">
        <div className="col-md-9">
          <div className="form-group">
            <div className="mb-2">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validate={handleValidate}
                onSubmit={handleSubmit}
              >
                {({ values, isValid, dirty, isSubmitting, setFieldValue }) => (
                  <Form>
                    <div className="col-sm form-group my-2">
                      <label>
                        Annual income range
                        <span className="text-danger">*</span>
                      </label>
                      <Field
                        as="select"
                        name="anual_income"
                        type="text"
                        className="form-select"
                      >
                        <option value={""}>Select an option</option>
                        {ANNUAL_TYPES.map((item) => {
                          return (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          );
                        })}
                      </Field>
                      <ErrorMessage
                        name="anual_income"
                        className="inline-errormsg my-1"
                        component="div"
                      />
                    </div>

                    {ANNUAL_TYPES.slice(2).includes(values.anual_income) && (
                      <>
                        <div className="col-sm form-group my-2">
                          <label>
                            Proof Type<span className="text-danger">*</span>
                          </label>
                          <Field
                            as="select"
                            name="icp_type"
                            type="text"
                            className="form-select"
                          >
                            <option value={""}>Select an option</option>
                            {PROOF_TYPES.map((item) => {
                              return (
                                <option
                                  key={item}
                                  value={item}
                                >
                                  {item}
                                </option>
                              );
                            })}
                          </Field>
                          <ErrorMessage
                            name="icp_type"
                            className="inline-errormsg my-1"
                            component="div"
                          />
                        </div>

                        <div className="form-group my-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <label>
                              Upload Proof{" "}
                              <span className="text-danger">*</span>
                            </label>
                          </div>
                          <Field name="icp_proof">
                            {(props) => (
                              <div className="file-uploader-container">
                                <FileUploader
                                  {...props}
                                  handleChange={(file) =>
                                    uploadFile(file, setFieldValue)
                                  }
                                  name="file"
                                  types={["PDF"]}
                                />
                              </div>
                            )}
                          </Field>
                          <div className="d-flex justify-content-between">
                            {values?.icp_proof && (
                              <p>
                                <small>
                                  File name: {values.icp_proof?.name}
                                </small>
                              </p>
                            )}
                            <ErrorMessage
                              name="icp_proof"
                              className="inline-errormsg my-1"
                              component="div"
                            />
                            <div className="file_limit_text">
                              Maximum file size: 4 MB
                            </div>
                          </div>
                        </div>

                        {values.is_encrypted === 1 && (
                          <div className="form-group my-2">
                            <label>
                              Proof passcode
                              <span className="text-danger">*</span>
                            </label>
                            <Field
                              type="text"
                              name="passcode"
                              className="form-control"
                              required
                            />
                          </div>
                        )}
                      </>
                    )}

                    <div className="d-flex form-group my-4">
                      <div className="">
                        <Button
                          type="button"
                          title="Back"
                          handleClick={() => setStep("PersonalDetails")}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="mx-2">
                        <Button
                          title="Submit"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomeProof;
